/**
 * @file: ChatHeaderTitle.tsx
 *
 * Header title for the chat conversation screen: an optional online dot +
 * the room/peer name, with an optional second line for the presence
 * (在线/离线) or typing state. Laid out as a centred column in normal flow —
 * the whole block is vertically centred in the header band, so it renders
 * consistently on the native nav header and on web (the previous
 * absolute-positioned subtitle clipped on the shorter native header band).
 *
 * The title string is passed as `children` (the navigator's headerTitle
 * contract); a `\n` splits it into the title line + the subtitle line.
 */

import { PresenceDot } from '@euler/app/flows/chat/components/PresenceDot';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { FC, ReactNode, memo } from 'react';
import { Text as RNText, StyleProp, View, ViewStyle } from 'react-native';

export const ChatHeaderTitle: FC<{
  children?: ReactNode;
  style?: StyleProp<ViewStyle>;
  /** Show the online dot beside the name (private chat with the peer online). */
  online?: boolean;
}> = memo(({ children, style, online }) => {
  const { rpx } = useResponsiveContext();
  const raw = typeof children === 'string' ? children : '';
  const [title, subtitle] = raw.includes('\n') ? raw.split('\n') : [raw, ''];
  return (
    <View
      style={[
        {
          flex: 1,
          alignSelf: 'stretch',
          alignItems: 'center',
          justifyContent: 'center',
        },
        style,
      ]}
    >
      <View
        style={{ flexDirection: 'row', alignItems: 'center', maxWidth: '100%' }}
      >
        {online ? <PresenceDot style={{ marginRight: rpx(5) }} /> : null}
        <RNText
          numberOfLines={1}
          style={{
            color: '#111',
            fontSize: rpx(17),
            lineHeight: rpx(22),
            fontFamily: FontFamily.NotoSans.Bold,
            flexShrink: 1,
          }}
        >
          {title}
        </RNText>
      </View>
      {subtitle ? (
        <RNText
          numberOfLines={1}
          style={{
            marginTop: rpx(1),
            color: 'rgba(17, 17, 17, 0.45)',
            fontSize: rpx(11),
            lineHeight: rpx(14),
            fontFamily: FontFamily.NotoSans.Light,
          }}
        >
          {subtitle}
        </RNText>
      ) : null}
    </View>
  );
});
